import React from "react";
import ReactDOM, { render } from "react-dom";

import App, { ReactComponent } from "@/App";
import Exception from "@/components/Exception";
import { GlobalConsumer, GlobalProvider, IGlobalContext } from "@/contexts";
import { LocaleProvider } from "@/i18n";
import IFrameService from "@/service/IFrame";
import { IconsProvider } from "@/styles";
import { queryToJSON } from "@/utils/transform";

const ROOT_NODE: HTMLElement | null = document.getElementById("root");

if (!ROOT_NODE) {
  throw new Error("Could not find container element with id 'root'.");
}

/**
 * @class ApplicationLoader
 * @description Creates entry point for Intractive apps as base class.
 *              See README for integration details.
 */
export default class ApplicationLoader {
  private readonly container: ReactComponent;
  private readonly query: IQuery;

  constructor(container: ReactComponent) {
    this.container = container;
    const searchParams = queryToJSON(window.location.search.slice(1));
    this.query = {
      id: searchParams.id,
      preview:
        searchParams.preview?.toLowerCase() === "true" ||
        searchParams.preview === "1",
      platform: searchParams.platform,
      locale: searchParams.locale,
      theme: searchParams.theme,
      category: searchParams.category,
    };
  }

  public unmount(): void {
    if (ROOT_NODE) {
      ReactDOM.unmountComponentAtNode(ROOT_NODE);
    }
    IFrameService.destroy();
  }

  public getInteractionId(): string {
    return this.query.id || "";
  }

  /**
   * @func start
   * Base class for application load
   */
  public start(): void {
    // Compose Container as Application
    const AppWrapper = App(this.container);

    render(
      <GlobalProvider value={{ query: this.query }}>
        <Exception>
          <GlobalConsumer>
            {(context: IGlobalContext) => {
              if (context.error) {
                throw context.error;
              }
              if (!context.response) {
                return <React.Fragment />;
              }
              if (!context.brand) {
                throw new Error("Unable to detect brand.");
              }
              return (
                <LocaleProvider locale={context.locale}>
                  <IconsProvider theme={context.brand.theme}>
                    <AppWrapper
                      {...context.response}
                      brand={context.brand}
                      onDestroy={this.unmount}
                    />
                  </IconsProvider>
                </LocaleProvider>
              );
            }}
          </GlobalConsumer>
        </Exception>
      </GlobalProvider>,
      ROOT_NODE
    );
  }
}
